import React, { ReactElement, useEffect, useRef, useState } from "react";
import { Text, Switch, Button, Icon } from "@jarvis-catalyst/ui-components";
import ModalEditTextWithEditor from "../../../components/Modal/editTextWithEditor";
import { OPTInList } from "../../../utils/listOptions";
import { ConstantValuesToGroup, GetUpdateVersionInput } from "../../../types";
import { UseFormReturn } from "react-hook-form";
import { NewVersionConsentConfigType } from "../../../types/newVersionConsentConfig";
import {
  CMRPUNTOS_DESCRIPTION,
  GCP_BUCKET_PDF_URL,
  LOYALTY_TEMPLATE_CODE_NAME,
  PRIVACY_TEMPLATE_CODE_NAME
} from "../../../config/constants";
import { UpdateAndPreviewPdf } from "../uploadAndPreviewPdf";
import { openPdfInNewTab } from "../../../utils/processPdfDocument";

type Props = {
  consent: NewVersionConsentConfigType;
  position: number;
  useStateForm: UseFormReturn<ConstantValuesToGroup, any>;
  templates: NewVersionConsentConfigType[];
  purpose: string;
};

const UpdateConsentComponent = ({ consent, position, useStateForm, purpose }: Props): ReactElement => {
  const keyTemplate = `template-${consent.group}-${position}`;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [titleModal, setTitleModal] = useState("");

  const previousDisplayTextRef = useRef<string>(consent.display_text);
  const previousCategoryTypeRef = useRef<string>(consent.category_type);
  const previousMandatoryRef = useRef<boolean>(consent.is_mandatory);
  const previousLegalTextRef = useRef<string>(consent.legal_text);
  const previousExplicitAgreeRef = useRef<boolean>(consent.rules?.explicitAgree || false);
  const previousVersionRef = useRef<string>(consent.version);

  const { formState, setValue, getValues } = useStateForm;
  const [explicitAgree, setExplicitAgree] = useState(consent?.rules?.explicitAgree);

  const [pdfViewerAttributes, setPdfViewerAttributes] = useState({
    title: "",
    content: ""
  });

  const incrementVersion = (version: string): string => {
    let newVersion: string = (Number(version) + 1).toString();
    newVersion = newVersion.padStart(3, "0");
    return newVersion;
  };

  const decreaseVersion = (version: string): string => {
    let newVersion: string = (Number(version) - 1).toString();
    newVersion = newVersion.padStart(3, "0");
    return newVersion;
  };

  function getUpdatedVersion({
    currentVersion,
    previousVersion,
    legalTextChanged,
    isNewElement
  }: GetUpdateVersionInput): string {
    const versionAreEqual = currentVersion === previousVersion;
    const isHigherVersion = Number(currentVersion) > Number(previousVersion);

    if (!legalTextChanged && isHigherVersion) {
      return decreaseVersion(currentVersion);
    }

    if (!isNewElement && legalTextChanged && versionAreEqual) {
      return incrementVersion(currentVersion);
    }

    return currentVersion;
  }

  const isPurposeNameCmrpuntos: boolean = Boolean(purpose.toLowerCase().includes("cmrpuntos"));

  const isLoyalty: boolean = Boolean(consent.name.toLowerCase().includes(LOYALTY_TEMPLATE_CODE_NAME));
  const isPolicy: boolean = Boolean(consent.name.toLowerCase().includes(PRIVACY_TEMPLATE_CODE_NAME));
  const isCMRpuntosTemplate: boolean = isLoyalty || isPolicy;
  const disabledInput: boolean = isCMRpuntosTemplate ? !isPurposeNameCmrpuntos : false;
  const isNewElement: boolean = Boolean(consent.newElement);
  const disableCode: boolean =
    (isNewElement && isCMRpuntosTemplate && !isPurposeNameCmrpuntos) ||
    (!isNewElement && isCMRpuntosTemplate) ||
    (!isNewElement && !isCMRpuntosTemplate);

  useEffect(() => {
    setValue(`${keyTemplate}.position`, position);
    setValue(`${keyTemplate}.group`, Number(consent.group));
    setValue(`${keyTemplate}.simpleText`, consent.category_type);
    setValue(`${keyTemplate}.displayText`, consent.display_text);
    setValue(`${keyTemplate}.mandatory`, consent.is_mandatory ? "yes" : "no");
    setValue(`${keyTemplate}.code`, consent.name);
    setValue(`${keyTemplate}.version`, consent.version);
    setValue(`${keyTemplate}.legalText`, consent.legal_text_path ?? consent.legal_text);
    setValue(`${keyTemplate}.explicitAgree`, explicitAgree ? explicitAgree : false);
  });

  useEffect(() => {
    consent.toUpdate = false;

    if (!consent.toCreate) {
      const isCategoryTypeChanged: boolean = previousCategoryTypeRef.current !== consent.category_type;
      const isDisplayTextChanged: boolean = previousDisplayTextRef.current !== consent.display_text;
      const isMandatoryChanged: boolean = previousMandatoryRef.current !== consent.is_mandatory;
      const isExplicitAgreeChanged: boolean =
        previousExplicitAgreeRef.current !== consent.rules?.explicitAgree;

      if (isCategoryTypeChanged || isDisplayTextChanged || isMandatoryChanged || isExplicitAgreeChanged) {
        consent.toUpdate = true;
      }
    }

    previousCategoryTypeRef.current = consent.category_type;
    previousDisplayTextRef.current = consent.display_text;
    previousMandatoryRef.current = consent.is_mandatory;
    previousExplicitAgreeRef.current = consent.rules?.explicitAgree ?? false;
  }, [consent.category_type, consent.display_text, consent.is_mandatory, consent.rules?.explicitAgree]);

  useEffect(() => {
    previousVersionRef.current = consent.version;
  }, [consent.id]);

  useEffect(() => {
    if (!getValues(`${keyTemplate}.legalText`)) {
      setValue(`${keyTemplate}.legalText`, consent.legal_text_path ?? consent.legal_text);
    }
  }, []);

  useEffect(() => {
    if (pdfViewerAttributes.content.length > 0) {
      consent.version = getUpdatedVersion({
        currentVersion: consent.version,
        previousVersion: previousVersionRef.current,
        legalTextChanged: true,
        isNewElement: Boolean(consent.newElement)
      });
    }
  }, [pdfViewerAttributes.content]);

  const handleOnSave = (e: any) => {
    const newVersion = getUpdatedVersion({
      currentVersion: consent.version,
      previousVersion: previousVersionRef.current,
      legalTextChanged: previousLegalTextRef.current !== consent.legal_text,
      isNewElement: Boolean(consent.newElement)
    });

    const versionAreEqual = consent.version === previousVersionRef.current;
    const isLegalTextChanged = previousLegalTextRef.current !== consent.legal_text;

    consent.version = newVersion;

    if (!consent.newElement) {
      consent.toCreate = isLegalTextChanged && versionAreEqual;

      if (consent.toCreate) {
        consent.is_enabled = false;
        const legalText = e.editor.getData();
        setValue(`${keyTemplate}.legalText`, legalText);
      }
    }
  };

  const handleTextLegalPreview = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    if (consent.legal_text_path) {
      openPdfInNewTab(`${GCP_BUCKET_PDF_URL}/${consent.legal_text_path}`);
    } else {
      setIsOpen(true);
      setTitleModal("Edit Legal Text");
    }
  };

  const handleTextLegalPreviewNewVersion = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    openPdfInNewTab(`${GCP_BUCKET_PDF_URL}/${consent.legal_text_path}`);
  };

  if (isOpen) {
    return (
      <ModalEditTextWithEditor
        isReadOnly={disabledInput}
        text={getValues(`${keyTemplate}.legalText`)}
        handleOnDestroy={(e: any) => handleOnSave(e)}
        setValue={(value: string) => {
          consent.legal_text = value;
        }}
        isOpen
        setIsOpen={setIsOpen}
        title={titleModal}
      />
    );
  }

  return (
    <>
      <div className="col-sm-2 d-flex justify-content-center">
        <Text>Position:</Text>
        <Text>{position}</Text>
      </div>
      <div className="col-sm-10 row mt-4 mb-4">
        <div className="mb-2">
          <Text>OPT-IN Category where</Text>
          <select
            disabled={disabledInput}
            {...useStateForm.register(`${keyTemplate}.simpleText`, {
              required: true,
              onChange: (e) => {
                e.preventDefault();
                e.stopPropagation();
                consent.category_type = e.target.value;
              }
            })}
            className={`form-control ${formState.errors[`${keyTemplate}`]?.simpleText ? "is-invalid" : ""}`}
          >
            {OPTInList().map((item, index) => (
              <option key={index} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-2">
          <div className="row">
            <Text>Display Text</Text>
            <div className="col-sm-10">
              <input
                disabled={disabledInput}
                {...useStateForm.register(`${keyTemplate}.displayText`, {
                  required: true,
                  onChange: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    consent.display_text = e.target.value;
                  }
                })}
                className={`form-control ${
                  formState.errors[`${keyTemplate}`]?.displayText ? "is-invalid" : ""
                }`}
                type="text"
              />
            </div>
          </div>
        </div>
        <div className="mb-5">
          <div className="row">
            <div className="col-sm-3">
              <Text>Mandatory</Text>
              <select
                disabled={disabledInput}
                className="form-select"
                {...useStateForm.register(`${keyTemplate}.mandatory`, {
                  required: true,
                  onChange: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    consent.is_mandatory = e.target.value === "yes";
                  }
                })}
              >
                <option value={"no"}>No</option>
                <option value={"yes"}>Yes</option>
              </select>
            </div>
            <div className="col-sm-6">
              <Text>Code</Text>
              <input
                disabled={disableCode}
                type="text"
                className={`form-control ${formState.errors[`${keyTemplate}`]?.code ? "is-invalid" : ""}`}
                {...useStateForm.register(`${keyTemplate}.code`, {
                  required: true,
                  onChange(event) {
                    event.preventDefault();
                    event.stopPropagation();
                    consent.name = event.target.value;
                  }
                })}
              />
            </div>
            <div className="col-sm-2">
              <Text>Version</Text>
              <input
                defaultValue={consent.version}
                disabled
                type="text"
                className={`form-control ${formState.errors[`${keyTemplate}`]?.version ? "is-invalid" : ""}`}
                {...useStateForm.register(`${keyTemplate}.version`, {
                  required: true,
                  disabled:
                    consent.name.includes(CMRPUNTOS_DESCRIPTION.PDP_CMR_PUNTOS) ||
                    consent.name.includes(CMRPUNTOS_DESCRIPTION.TYC_CMR_PUNTOS) ||
                    !consent.newElement,
                  onChange(event) {
                    event.preventDefault();
                    event.stopPropagation();
                    consent.version = event.target.value;
                  }
                })}
              />
            </div>
          </div>
        </div>
        <div className="mb-5">
          <div className="row">
            <div className="col-sm-6" style={{ marginTop: "-30px", marginBottom: "-15px" }}>
              <Text>Aceptación Explicita</Text>
              <Switch
                onChange={(value) => {
                  const rules = { ...consent.rules };
                  consent.rules = rules;
                  consent.rules.explicitAgree = value;
                  setExplicitAgree(value);
                  setValue(`${keyTemplate}.explicitAgree`, value, { shouldValidate: false });
                }}
                value={Boolean(explicitAgree)}
                size="sm"
              />
            </div>
          </div>
        </div>
        <div style={{ marginTop: "-30px" }}>
          <div className="row">
            <Text>Legal Text</Text>
            {consent.legal_text && (consent.legal_text_path?.length ?? 0) <= 0 && (
              <>
                <div className="col-sm-10">
                  <textarea
                    disabled
                    className={`form-control ${
                      formState.errors[`${keyTemplate}`]?.legalText ? "is-invalid" : ""
                    }`}
                    {...useStateForm.register(`${keyTemplate}.legalText`, {
                      required: true
                    })}
                  />
                </div>
                <div className="col-sm-2 d-flex align-items-center">
                  <Button onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleTextLegalPreview(e)}>
                    <Icon name="Edit" color="white" size={24} />
                  </Button>
                </div>
              </>
            )}
            {consent.legal_text_path &&
            (consent.name?.toLowerCase().includes(CMRPUNTOS_DESCRIPTION.TYC_CMR_PUNTOS) ||
              consent.name?.toLowerCase().includes(CMRPUNTOS_DESCRIPTION.PDP_CMR_PUNTOS)) &&
            consent.purpose?.purposeName?.toLowerCase() !== CMRPUNTOS_DESCRIPTION.EXPERIENCE_NAME ? (
              <>
                <div className="col-sm-10">
                  <textarea className="form-control" disabled value={consent.legal_text_path} />
                </div>
                <div className="col-sm-2 d-flex align-items-center">
                  <Button
                    onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                      handleTextLegalPreviewNewVersion(e)
                    }
                  >
                    <Icon name="View" color="white" size={24} />
                  </Button>
                </div>
              </>
            ) : (
              <>
                {consent.legal_text_path && (
                  <>
                    <div className="col-sm-10">
                      <textarea className="form-control" disabled value={consent.legal_text_path} />
                    </div>
                    <div className="col-sm-2 d-flex align-items-center">
                      <Button
                        onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                          handleTextLegalPreviewNewVersion(e)
                        }
                      >
                        <Icon name="View" color="white" size={24} />
                      </Button>
                    </div>
                  </>
                )}
                <div className="mb-3">
                  <UpdateAndPreviewPdf
                    consent={consent}
                    pdfViewerAttributes={pdfViewerAttributes}
                    setPdfViewerAttributes={setPdfViewerAttributes}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateConsentComponent;
